import { IUser, UserRole } from '~/types/user-info-types'
import { countryData } from '~/shared/constants/countryData.const'

import {
  City,
  GradeLevel,
  LevelOfEducation,
  SchoolSystem,
  Trait,
  PreferredTime,
  Subject,
  BookingLanguage,
  Timeslot,
  TutorLevel,
  BookingCurrency,
  EducationalDegree,
} from './booking'

export interface Booking {
  bookingOrderProducts?: {
    createdAt: string
    currency: BookingCurrency
    hours: number
    id: number
    mentorLevel: TutorLevel
    minutes: number
    orderId: number
    priceTotal: string
    productId: number
    updatedAt: string
  }
  createdAt: string
  id: number
  orderId: number
  preferenceId: number
  status: BookingStatus
  totalHours: number
  totalMinutes: number
  type: ProductCategory
  updatedAt: string
  userId: number
}

export interface TutorBooking extends Booking {
  properties: null
}

export interface VideoChatBooking extends Booking {
  properties: {
    scheduleAt: string
    needHelpWith?: string
  }
  sessions: Session[]
  student: Student
}

export type Tutor<T = ''> = {
  countryCallingCode?: string
  countryCode?: string
  description?: string
  institute?: string
  educationalDegree?: EducationalDegree
  mentorLevel: string
  preferenceId: number
  role: UserRole.TUTOR | UserRole.ADMIN
  status: TutorStatus
  userId: number
  zoomUserid?: string

  // Graphs
  mentorStudents?: Student[]
  mentorSessions?: Session[]
  preference?: Preference<T>
} & IUser

export type Student<T = ''> = {
  cancelledHours?: number
  completedHours?: number
  countryCallingCode?: string
  countryCode?: string
  hoursUsed?: number
  lastSession?: Session
  lateCancelledHours?: number
  latestBooking?: TutorBooking | VideoChatBooking
  minutesLeft?: number
  nextSession?: Session
  noShowcancelledHours?: number
  pendingHours?: number
  role: UserRole.USER
  totalHours?: number
  totalMinutes?: number
  userId: number

  // Graphs
  bookings?: Array<TutorBooking | VideoChatBooking>
  mentors?: Tutor[]
  preference?: Preference<T>
  sessions?: Session[]
  suggestedMentors?: Tutor[]
} & IUser

export interface Session {
  endTime: string
  id: number
  link?: string
  location?: string
  mentor?: Tutor
  mentorComment?: string | null
  mentorId: number
  sessionMentor?: Tutor
  sessionStudent?: SessionStudent
  startTime: string
  status: SessionStatus
  student?: SessionStudent
  studentId: number
  studentRating?: number | null
  type: ProductCategory
  meetings: { room: string; mentor: string; student: string }
  properties?: { needHelpWith?: string }
}
export interface SessionTutor {
  endTime: string
  id: number
  link?: string
  location?: string
  tutor?: Tutor
  tutorComment?: string | null
  tutorId: number
  sessionTutor?: Tutor
  sessionStudent?: SessionStudent
  startTime: string
  status: SessionStatus
  student?: SessionStudent
  studentId: number
  studentRating?: number | null
  type: ProductCategory
  meetings: { room: string; mentor: string; student: string }
  properties?: { needHelpWith?: string }
}

export type SessionStudent = Pick<
  Student,
  | 'avatarPngUrl'
  | 'birthYear'
  | 'country'
  | 'email'
  | 'facebookEmail'
  | 'fullName'
  | 'gradeLevelId'
  | 'id'
  | 'phone'
  | 'role'
  | 'skill'
  | 'timezone'
  | 'userType'
  | 'yearsInSchool'
>

export interface Preference<T = ''> {
  city?: City
  gradeLevels?: GradeLevel[]
  id: number
  languages?: BookingLanguage[]
  languagePrimary?: BookingLanguage
  languageSecondary?: BookingLanguage
  schoolCountry?: (typeof countryData)[number]
  schoolSystem?: SchoolSystem
  subjects?: Subject[]
  timeslots?: T extends 'from-api-response' ? Timeslot[] : PreferredTime[]
  levelsOfEducation?: LevelOfEducation[]
  traits?: Trait[]
}

enum ORDER_STATUS {
  PENDING = 'pending',
  PAID = 'paid',
  CANCELED = 'canceled',
}

export interface Receipt {
  id: number
  orderId: number // Reference
  hours: number
  subTotal: number
  travelExpense: number
  priceTotal: number
  currency: string
  mentorLevel: string
  createdAt: string
  status: ORDER_STATUS
}

export enum AssignmentStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
}

export enum SessionStatus {
  PENDING = 'pending',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
  CANCELLED_NO_SHOW = 'cancelled_no_show',
  CANCELLED_LATE = 'cancelled_late',
}

export enum BookingStatus {
  PENDING = 'pending',
  CONFIRMED = 'confirmed',
}

export enum TutorStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
}

export enum ProductCategory {
  TUTORING = 'tutoring',
  VIDEO_CHAT = 'videoChat',
}

export interface TutoringAppointment extends Session {
  __typename: 'tutoring'
}

export interface VideoChatShiftAppointment {
  __typename: 'video-chat-shift'
  endTime: string
  sessions: Session[]
  startTime: string
}

export type Appointment = Session | TutoringAppointment | VideoChatShiftAppointment
